// apps/web/src/routes/__root.tsx
/// <reference types="vite/client" />
import { QueryClientProvider } from '@tanstack/react-query'
import { createServerFn } from '@tanstack/react-start'
import * as React from 'react'
import {
  HeadContent,
  Link,
  Outlet,
  Scripts,
  createRootRoute,
  redirect,
  useLocation,
} from '@tanstack/react-router'
import { UserButton } from '@clerk/tanstack-react-start'
import { auth, clerkClient } from '@clerk/tanstack-react-start/server'
import { getRequestHost } from '@tanstack/start-server-core'
import clsx from 'clsx'
import appCss from '../index.css?url'
import { AppAuthProvider } from '../components/AppAuthProvider'
import { SignedIn, SignedOut } from '../lib/auth'
import { isAdminHost as checkIsAdminHost, isAdminPrincipal } from '../lib/adminAccess'
import { convexClient, convexQueryClient, queryClient } from '../router'

const fetchClerkAuth = createServerFn({ method: 'GET' }).handler(async () => {
  const authObj = await auth()
  const token = await authObj.getToken({ template: 'convex' })
  const host = getRequestHost({ xForwardedHost: true })
  const claims = (authObj.sessionClaims ?? {}) as Record<string, unknown>
  let emailClaim =
    (typeof claims.email === 'string' && claims.email) ||
    (typeof claims.email_address === 'string' && claims.email_address) ||
    null

  // Some Clerk token templates may not include email claims.
  // Fallback to Clerk Backend API so ADMIN_EMAILS gating still works reliably.
  if (!emailClaim && authObj.userId) {
    try {
      const user = await clerkClient().users.getUser(authObj.userId)
      emailClaim =
        user.emailAddresses.find((e) => e.id === user.primaryEmailAddressId)?.emailAddress ??
        user.emailAddresses[0]?.emailAddress ??
        null
    } catch {
      // Non-fatal: keep null and rely on ADMIN_USER_IDS if configured.
    }
  }

  return {
    userId: authObj.userId,
    token,
    host,
    email: emailClaim,
    isAdminHost: checkIsAdminHost(host),
    isAdminAllowed: checkIsAdminHost(host)
      ? Boolean(
          authObj.userId &&
            isAdminPrincipal({ userId: authObj.userId, email: emailClaim })
        )
      : false,
  }
})

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    const { userId, token, host, isAdminHost } = await fetchClerkAuth()

    if (token) {
      convexQueryClient.serverHttpClient?.setAuth(token)
    }

    const isAdminPath = location.pathname.startsWith('/admin')
    const isAuthPath =
      location.pathname === '/login' ||
      location.pathname === '/signup' ||
      location.pathname === '/sign-up' ||
      location.pathname.startsWith('/signup/')

    if (isAdminHost && !isAdminPath && !isAuthPath) {
      throw redirect({ to: '/admin/import-metrics' })
    }

    if (!isAdminHost && isAdminPath) {
      throw redirect({ to: '/app' })
    }

    return { userId, token, host, isAdminHost }
  },
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { name: 'title', content: 'MiseBook — Meal Planning for Couples' },
      { name: 'description', content: 'Finally agree on dinner. Import recipes, plan your week together, and generate grocery lists.' },
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
    ],
  }),
  component: RootComponent,
})

function RootComponent() {
  return (
    <AppAuthProvider client={convexClient}>
      <QueryClientProvider client={queryClient}>
        <RootDocument>
          <Outlet />
        </RootDocument>
      </QueryClientProvider>
    </AppAuthProvider>
  )
}

function RootDocument({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const { isAdminHost } = Route.useRouteContext()
  const isAuthPage = location.pathname === '/' || location.pathname === '/login' || location.pathname === '/signup' || location.pathname.startsWith('/join')
  const hideShell = isAdminHost
    ? location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/sign-up'
    : isAuthPage

  if (hideShell) {
    return (
      <html>
        <head>
          <HeadContent />
        </head>
        <body className="bg-snow">
          {children}
          <Scripts />
        </body>
      </html>
    )
  }

  return (
    <html>
      <head>
        <HeadContent />
      </head>
      <body className="bg-snow">
        <div className="min-h-screen flex flex-col">
          <Header />
          <main className="flex-1">{children}</main>
          <footer className="py-8 text-center border-t border-mist">
            <p className="text-stone text-sm">Made with ❤️ for home cooks</p>
          </footer>
        </div>
        <Scripts />
      </body>
    </html>
  )
}

function Header() {
  const { isAdminHost } = Route.useRouteContext()

  return (
    <header className="sticky top-0 z-50 bg-snow/80 backdrop-blur-md border-b border-mist">
      <div className="max-w-5xl mx-auto px-4">
        <div className="h-14 flex items-center justify-between">
          <Link to={isAdminHost ? '/admin/import-metrics' : '/'} className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-lg bg-coral-500 flex items-center justify-center shadow-sm">
              <span className="text-lg">🍽️</span>
            </div>
            <span className="font-display text-lg font-semibold text-ink hidden sm:block">
              {isAdminHost ? 'MiseBook Admin' : 'MiseBook'}
            </span>
          </Link>

          <nav className="flex items-center gap-1">
            {isAdminHost ? (
              <NavLink to="/admin/import-metrics" icon="📊">
                Import Metrics
              </NavLink>
            ) : (
              <>
                <NavLink to="/app" icon="📅">Plan</NavLink>
                <NavLink to="/recipes" icon="📖">Recipes</NavLink>
                <NavLink to="/shopping" icon="🛒">Shopping</NavLink>
                <NavLink to="/kitchen" icon="👥">Kitchen</NavLink>
              </>
            )}
            <AuthActions />
          </nav>
        </div>
      </div>
    </header>
  )
}

function AuthActions() {
  return (
    <div className="ml-2 flex items-center">
      <SignedIn>
        <UserButton
          afterSignOutUrl="/login"
          appearance={{
            elements: {
              userButtonAvatarBox:
                'w-8 h-8 rounded-full ring-1 ring-coral-100',
            },
          }}
        />
      </SignedIn>
      <SignedOut>
        <Link to="/login" className="btn btn-primary text-sm">
          Sign in
        </Link>
      </SignedOut>
    </div>
  )
}

function NavLink({ to, icon, children }: { to: string; icon: string; children: React.ReactNode }) {
  const location = useLocation()
  const isActive = to === '/app' 
    ? location.pathname === '/app' || location.pathname === '/' 
    : location.pathname.startsWith(to)
  
  return (
    <Link
      to={to}
      className={clsx(
        'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150',
        isActive ? 'bg-coral-50 text-coral-600' : 'text-stone hover:text-ink hover:bg-sand'
      )}
    >
      <span className="text-base">{icon}</span>
      <span className="hidden sm:inline">{children}</span>
    </Link>
  )
}
